import React from 'react';
import { createFeature } from '@edreamsodigeo/frontend-core';
import { useLogin } from '@/providers/login/LoginProvider';
import {
  PRIME_FIRST_PHASE_2_DESKTOP,
  PRIME_FIRST_PHASE_2_MOBILE,
} from '@/versionedComponents';
import { useIsPrimeUser } from '@/hooks/useIsPrimeUser';
import { OFFERS, PAYMENT } from '@/hooks/usePriceFreezeRouter';
import {
  FIELDS,
  FIELDS_WITH_CONFIRM,
} from '@/components/SummaryContactDetails/formUtils';
import { HomepageComponent } from '@/components/HomePage/HomepageComponent';
import { useResetTMSTracking, useTrackTMSPage } from '@/hooks/useTcVars';
import {
  useActionsToOffersPage,
  useActionsToPaymentPage,
  useOnContinueSuccess,
} from './HomePageIRUM.hooks';
import { withFlowComponent } from '@/HOC/withFlowComponent';
import { withNativeAware } from '@/HOC/withNativeAware';

const NativeVersionedMobileHomepage = createFeature({
  name: PRIME_FIRST_PHASE_2_MOBILE,
  variations: [
    HomePageRecognize,
    HomePagePrimeFirstPhase2,
    HomePagePrimeFirstPhase2,
  ],
  version: 1,
});
export const VersionedMobileHomepage = withNativeAware(
  NativeVersionedMobileHomepage,
  HomePagePrimeFirstPhase2
);

const VersionedDesktopHomepage = createFeature({
  name: PRIME_FIRST_PHASE_2_DESKTOP,
  variations: [
    HomePageRecognize,
    HomePagePrimeFirstPhase2,
    HomePagePrimeFirstPhase2,
  ],
  version: 1,
});

export const HomePageIRUM = withFlowComponent(
  HomePageIRUMMobile,
  HomePageIRUMDesktop
);

function HomePageIRUMMobile(props) {
  useTrackTMSPage('summary');
  return <VersionedMobileHomepage {...props} />;
}

function HomePageIRUMDesktop(props) {
  return <VersionedDesktopHomepage {...props} />;
}

function HomePageRecognize({ searchId, fareItineraryKey, segmentKeys }, props) {
  // when the user clicks CONTINUE:
  // - create the SB
  // - create the PF product
  // - navigate to Payment page
  const isPrimeUser = useIsPrimeUser();
  const onContinueSuccess = useOnContinueSuccess({
    searchId,
    fareItineraryKey,
    segmentKeys,
  });

  const { loading, error, onContinueClick } = useActionsToPaymentPage({
    onContinueSuccess: () => onContinueSuccess(PAYMENT),
  });

  return (
    <HomepageComponent
      {...props}
      formFields={FIELDS}
      loading={loading}
      error={error}
      validation={() => Boolean(isPrimeUser)}
      onContinue={onContinueClick}
    />
  );
}

function HomePagePrimeFirstPhase2(
  { searchId, fareItineraryKey, segmentKeys },
  props
) {
  // when the user clicks CONTINUE:
  // - create the SB
  // - if the user is Prime or recognized Prime
  // - - create the price freeze product
  // - - navigate to Payment page
  // - else
  // - - add the contact details to the SB
  // - - navigate to Offers page
  const { isLogged } = useLogin();
  const isPrimeUser = useIsPrimeUser();
  useResetTMSTracking(isPrimeUser);
  const onContinueSuccess = useOnContinueSuccess({
    searchId,
    fareItineraryKey,
    segmentKeys,
  });

  // Behavior for non logged users
  const {
    loading: loadingOffers,
    error: errorOffers,
    onContinueClick: onContinueClickToOffersPage,
  } = useActionsToOffersPage({
    onContinueSuccess: () => onContinueSuccess(OFFERS),
  });
  ///////////////////////////////
  // Behaviour for prime and recognized prime users
  const {
    loading: loadingPayment,
    error: errorPayment,
    onContinueClick: onContinueClickToPaymentPage,
  } = useActionsToPaymentPage({
    onContinueSuccess: () => onContinueSuccess(PAYMENT),
  });
  //

  return (
    <HomepageComponent
      {...props}
      formFields={isLogged ? FIELDS : FIELDS_WITH_CONFIRM}
      loading={loadingOffers || loadingPayment}
      error={errorOffers || errorPayment}
      onContinue={(formData) => {
        if (isPrimeUser) {
          onContinueClickToPaymentPage(formData);
        } else {
          onContinueClickToOffersPage(formData);
        }
      }}
    />
  );
}
